import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import { useCan } from "../../auth/Can";
import { getQaReview, QaReview, submitQaReview } from "../../services/supportService";

/** Phase 2 §5.10: a lead's QA review of a finished ticket (5-point checklist, one per ticket, never your own). */
export const QA_CHECK_LABELS: Record<string, string> = { greeting: "Greeting", correctAnswer: "Correct answer", policy: "Followed policy", tone: "Tone", closure: "Closure" };
const KEYS = Object.keys(QA_CHECK_LABELS);

const QaReviewPanel: React.FC<{ ticketId: string | number }> = ({ ticketId }) => {
    const { can, canAny } = useCan();
    const [review, setReview] = useState<QaReview | null | undefined>(undefined);
    const [ticks, setTicks] = useState<Record<string, boolean>>(Object.fromEntries(KEYS.map((k) => [k, false])));
    const [note, setNote] = useState("");
    const [error, setError] = useState<string | null>(null);

    const load = () => getQaReview(ticketId).then((r) => setReview(r.data.review)).catch(() => setReview(null));
    useEffect(() => { load(); }, [ticketId]); // eslint-disable-line react-hooks/exhaustive-deps

    const save = async () => {
        try {
            await submitQaReview(ticketId, { ...ticks, ...(note.trim() ? { note: note.trim() } : {}) } as any);
            setError(null);
            load();
        } catch (e: any) {
            setError(e?.response?.data?.message || "Could not save the review");
        }
    };

    if (review === undefined) return null;
    return (
        <Box sx={{ mt: 2, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }} data-testid="qa-review-panel">
            <Typography variant="caption" sx={{ fontWeight: 700 }}>QA REVIEW</Typography>
            {review ? (
                <>
                    <Typography variant="body2">Score {review.score}/5 — {KEYS.filter((k) => (review as any)[k]).map((k) => QA_CHECK_LABELS[k]).join(", ") || "no ticks"}</Typography>
                    {review.note && <Typography variant="body2" color="text.secondary">{review.note}</Typography>}
                </>
            ) : can("support:write") && (canAny(["support:lead", "wallet:adjust"] as any)) ? (
                <>
                    <Stack direction="row" flexWrap="wrap" useFlexGap>
                        {KEYS.map((k) => (
                            <FormControlLabel key={k} label={QA_CHECK_LABELS[k]} control={<Checkbox size="small" checked={ticks[k]} onChange={(e) => setTicks((t) => ({ ...t, [k]: e.target.checked }))} />} />
                        ))}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField size="small" label="QA note" value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 500 }} sx={{ minWidth: 220 }} />
                        <Button size="small" variant="outlined" onClick={save}>Save review</Button>
                    </Stack>
                </>
            ) : (
                <Typography variant="body2" color="text.secondary">Not reviewed yet.</Typography>
            )}
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </Box>
    );
};

export default QaReviewPanel;
